import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, BarChart3, Crown, Image, Activity, LogOut, RefreshCcw, Search } from 'lucide-react';

const API = `${window.location.origin}/api`;

const adminApi = axios.create();
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('muno_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem('muno_admin_token', res.data.token);
      onLogin(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-sm bg-gray-900 rounded-2xl p-8 border border-cyan-500/20 shadow-2xl">
        <h1 className="text-2xl font-black text-white text-center mb-1" style={{ fontFamily: "'Unbounded', sans-serif" }}>MUNOMUSIC</h1>
        <p className="text-cyan-400 text-xs text-center mb-8 font-bold uppercase tracking-widest">Painel Administrativo</p>
        
        {error && <div className="mb-4 p-3 rounded-xl bg-red-900/30 border border-red-500/30 text-red-300 text-xs text-center">{error}</div>}
        
        <div className="space-y-4">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full p-4 rounded-xl bg-gray-800 border border-gray-700 text-white outline-none focus:ring-2 ring-cyan-500/50 text-sm"
            data-testid="admin-email"
            required
          />
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Senha"
            className="w-full p-4 rounded-xl bg-gray-800 border border-gray-700 text-white outline-none focus:ring-2 ring-cyan-500/50 text-sm"
            data-testid="admin-password"
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full p-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-black uppercase tracking-wider text-sm hover:from-cyan-400 hover:to-blue-500 transition-all disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </div>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-gray-900 rounded-2xl p-5 border border-gray-800 hover:border-gray-700 transition-all">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-xl ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
        <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-3xl font-black text-white">{value}</p>
    </div>
  );
}

function AdminDashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tab, setTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, actRes] = await Promise.all([
        adminApi.get(`${API}/admin/stats`),
        adminApi.get(`${API}/admin/users`),
        adminApi.get(`${API}/admin/activity`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setActivities(actRes.data.activities);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    }
    setLoading(false);
  }, [onLogout]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('muno_admin_token');
    onLogout();
  };

  const filteredUsers = users.filter(u => 
    (u.name || '').toLowerCase().includes(search.toLowerCase()) || 
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <RefreshCcw size={32} className="text-cyan-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-black text-cyan-400" style={{ fontFamily: "'Unbounded', sans-serif" }}>MUNOMUSIC</h1>
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-800 px-2 py-1 rounded">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchData} className="p-2 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-gray-800 transition-all" title="Atualizar">
              <RefreshCcw size={16} />
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-all text-xs font-bold">
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
            { id: 'users', label: 'Usuários', icon: Users },
            { id: 'activity', label: 'Atividades', icon: Activity }
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                tab === t.id ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-900'
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Usuários" value={stats.total_users} color="bg-blue-600" />
              <StatCard icon={Crown} label="Usuários Pro" value={stats.pro_users} color="bg-yellow-600" />
              <StatCard icon={BarChart3} label="Prompts Hoje" value={stats.prompts_today} color="bg-green-600" />
              <StatCard icon={Image} label="Capas Hoje" value={stats.covers_today} color="bg-purple-600" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Usuários Free" value={stats.free_users} color="bg-gray-600" />
              <StatCard icon={Crown} label="Taxa Pro" value={stats.total_users ? `${Math.round(stats.pro_users/stats.total_users*100)}%` : '0%'} color="bg-orange-600" />
              <StatCard icon={BarChart3} label="Total Prompts" value={stats.total_prompts} color="bg-cyan-600" />
              <StatCard icon={Image} label="Total Capas" value={stats.total_covers} color="bg-pink-600" />
            </div>

            {/* Daily Chart */}
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
              <h3 className="text-sm font-black text-gray-300 mb-4 uppercase tracking-wider">Prompts por Dia (últimos 7 dias)</h3>
              <div className="flex items-end gap-2 h-40">
                {stats.daily_stats.map((d, i) => {
                  const max = Math.max(...stats.daily_stats.map(s => s.count), 1);
                  const height = (d.count / max) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-gray-400 font-bold">{d.count}</span>
                      <div 
                        className="w-full bg-gradient-to-t from-cyan-600 to-cyan-400 rounded-t-lg transition-all" 
                        style={{ height: `${Math.max(height, 4)}%` }}
                      />
                      <span className="text-[9px] text-gray-500">{d.date.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div className="space-y-4">
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar por nome ou email..."
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-gray-900 border border-gray-800 text-white outline-none focus:ring-2 ring-cyan-500/30 text-sm"
                data-testid="admin-user-search"
              />
            </div>
            <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Nome</th>
                      <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Plano</th>
                      <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Prompts</th>
                      <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Capas</th>
                      <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Criado em</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u, i) => (
                      <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-all">
                        <td className="px-4 py-3 text-sm text-white font-medium">{u.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-md text-[10px] font-black uppercase ${
                            u.plan === 'pro' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700/50 text-gray-400'
                          }`}>
                            {u.plan || 'free'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-cyan-400 font-bold">{u.total_prompts}</td>
                        <td className="px-4 py-3 text-sm text-purple-400 font-bold">{u.total_covers}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredUsers.length === 0 && (
                <p className="text-center py-8 text-gray-500 text-sm">Nenhum usuário encontrado</p>
              )}
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {tab === 'activity' && (
          <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Data/Hora</th>
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Usuário</th>
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Ação</th>
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Modo</th>
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Gênero</th>
                    <th className="px-4 py-3 text-[10px] font-black text-gray-500 uppercase tracking-wider">Idioma</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((a, i) => (
                    <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-all">
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {a.timestamp ? new Date(a.timestamp).toLocaleString('pt-BR') : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-white">{a.name || a.email || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-md text-[10px] font-black uppercase ${
                          a.action === 'generate_cover' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'
                        }`}>
                          {a.action === 'generate_cover' ? 'Capa' : 'Prompt'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 capitalize">{a.mode || '-'}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{a.genre || '-'}{a.genre2 ? ` + ${a.genre2}` : ''}</td>
                      <td className="px-4 py-3 text-xs text-gray-400 uppercase">{a.language || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {activities.length === 0 && (
              <p className="text-center py-8 text-gray-500 text-sm">Nenhuma atividade registrada ainda</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('muno_admin_token');
    if (token) {
      adminApi.get(`${API}/admin/me`)
        .then(res => { setAdmin(res.data); setChecking(false); })
        .catch(() => { localStorage.removeItem('muno_admin_token'); setChecking(false); });
    } else {
      setChecking(false);
    }
  }, []);

  if (checking) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <RefreshCcw size={32} className="text-cyan-400 animate-spin" />
    </div>
  );

  if (!admin) return <AdminLogin onLogin={setAdmin} />;

  return <AdminDashboard onLogout={() => setAdmin(null)} />;
}
